/**
 * Global Pagination Configuration
 *
 * Centralized configuration for pagination, data display, and navigation patterns
 * across the application. This ensures consistent data browsing experiences
 * throughout all components and tables.
 */

import type { CSSProperties } from 'react';

export interface PaginationConfig {
  /** Default pagination settings */
  defaults: {
    pageSize: number;
    showFirstLast: boolean;
    showPrevNext: boolean;
    showPageNumbers: boolean;
    showPageSizeSelector: boolean;
    showItemCounts: boolean;
    maxVisiblePages: number;
    enableJumpToPage: boolean;
  };
  
  /** Page size options */
  pageSizeOptions: {
    default: number[];
    compact: number[];
    extended: number[];
    custom: number[];
  };
  
  /** Navigation button configurations */
  buttons: {
    first: {
      enabled: boolean;
      text: string;
      icon: string;
      style: CSSProperties;
      hoverStyle: CSSProperties;
      disabledStyle: CSSProperties;
    };
    previous: {
      enabled: boolean;
      text: string;
      icon: string;
      style: CSSProperties;
      hoverStyle: CSSProperties;
      disabledStyle: CSSProperties;
    };
    next: {
      enabled: boolean;
      text: string;
      icon: string;
      style: CSSProperties;
      hoverStyle: CSSProperties;
      disabledStyle: CSSProperties;
    };
    last: {
      enabled: boolean;
      text: string;
      icon: string;
      style: CSSProperties;
      hoverStyle: CSSProperties;
      disabledStyle: CSSProperties;
    };
    pageNumber: {
      style: CSSProperties;
      activeStyle: CSSProperties;
      hoverStyle: CSSProperties;
    };
    ellipsis: {
      style: CSSProperties;
      text: string;
    };
  };
  
  /** Display formats */
  display: {
    itemCounts: {
      format: 'simple' | 'detailed' | 'compact';
      templates: {
        simple: string;      // "1-10 of 100"
        detailed: string;    // "Showing 1 to 10 of 100 results"
        compact: string;     // "1-10/100"
      };
      position: 'top' | 'bottom' | 'both';
    };
    pageNumbers: {
      format: 'numbers' | 'dots' | 'mixed';
      showEllipsis: boolean;
      ellipsisThreshold: number;
      maxVisible: number;
    };
    pageSize: {
      label: string;
      suffix: string;
      position: 'left' | 'right' | 'center';
    };
  };
  
  /** Behavior settings */
  behavior: {
    jumpToPage: {
      enabled: boolean;
      trigger: 'click' | 'doubleClick' | 'rightClick';
      inputType: 'number' | 'dropdown';
      validation: boolean;
      placeholder: string;
    };
    infiniteScroll: {
      enabled: boolean;
      threshold: number;          // Items remaining before loading more
      loadingBuffer: number;      // Additional items to load
      maxAutoLoad: number;        // Max items before requiring pagination
      loadingIndicator: boolean;
    };
    keyboard: {
      enabled: boolean;
      shortcuts: {
        nextPage: string[];
        previousPage: string[];
        firstPage: string[];
        lastPage: string[];
        jumpToPage: string[];
      };
    };
    url: {
      updateUrl: boolean;
      pageParam: string;
      sizeParam: string;
      preserveOtherParams: boolean;
    };
  };
  
  /** Styling variants */
  variants: {
    default: {
      container: CSSProperties;
      spacing: string;
      alignment: 'left' | 'center' | 'right' | 'space-between';
    };
    compact: {
      container: CSSProperties;
      spacing: string;
      alignment: 'left' | 'center' | 'right' | 'space-between';
    };
    minimal: {
      container: CSSProperties;
      spacing: string;
      alignment: 'left' | 'center' | 'right' | 'space-between';
    };
    detailed: {
      container: CSSProperties;
      spacing: string;
      alignment: 'left' | 'center' | 'right' | 'space-between';
    };
  };
  
  /** Responsive behavior */
  responsive: {
    breakpoints: {
      mobile: number;
      tablet: number;
      desktop: number;
    };
    adaptations: {
      mobile: {
        showFirstLast: boolean;
        showPageNumbers: boolean;
        maxVisiblePages: number;
        compactLabels: boolean;
      };
      tablet: {
        showFirstLast: boolean;
        showPageNumbers: boolean;
        maxVisiblePages: number;
        compactLabels: boolean;
      };
    };
  };
  
  /** Loading and error states */
  states: {
    loading: {
      showSkeleton: boolean;
      skeletonStyle: CSSProperties;
      loadingText: string;
      showProgress: boolean;
    };
    error: {
      showRetry: boolean;
      retryText: string;
      errorMessageStyle: CSSProperties;
    };
    empty: {
      showMessage: boolean;
      emptyText: string;
      showCreateButton: boolean;
      createButtonText: string;
    };
  };
}

export const paginationConfig: PaginationConfig = {
  // Default settings
  defaults: {
    pageSize: 25,
    showFirstLast: true,
    showPrevNext: true,
    showPageNumbers: true,
    showPageSizeSelector: true,
    showItemCounts: true,
    maxVisiblePages: 5,
    enableJumpToPage: true,
  },

  // Page size options
  pageSizeOptions: {
    default: [10, 25, 50, 100],
    compact: [5, 10, 25],
    extended: [10, 25, 50, 100, 250, 500],
    custom: [20, 40, 60],
  },

  // Navigation buttons
  buttons: {
    first: {
      enabled: true,
      text: 'First',
      icon: '⟪',
      style: {
        padding: '8px 12px',
        backgroundColor: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
        borderRadius: '6px',
        color: 'hsl(var(--foreground))',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 150ms ease',
      },
      hoverStyle: {
        backgroundColor: 'hsl(var(--accent))',
        borderColor: 'hsl(var(--muted-foreground))',
      },
      disabledStyle: {
        backgroundColor: 'hsl(var(--muted))',
        color: 'hsl(var(--muted-foreground))',
        cursor: 'not-allowed',
        opacity: 0.6,
      },
    },
    previous: {
      enabled: true,
      text: 'Previous',
      icon: '‹',
      style: {
        padding: '8px 12px',
        backgroundColor: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
        borderRadius: '6px',
        color: 'hsl(var(--foreground))',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 150ms ease',
      },
      hoverStyle: {
        backgroundColor: 'hsl(var(--accent))',
        borderColor: 'hsl(var(--muted-foreground))',
      },
      disabledStyle: {
        backgroundColor: 'hsl(var(--muted))',
        color: 'hsl(var(--muted-foreground))',
        cursor: 'not-allowed',
        opacity: 0.6,
      },
    },
    next: {
      enabled: true,
      text: 'Next',
      icon: '›',
      style: {
        padding: '8px 12px',
        backgroundColor: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
        borderRadius: '6px',
        color: 'hsl(var(--foreground))',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 150ms ease',
      },
      hoverStyle: {
        backgroundColor: 'hsl(var(--accent))',
        borderColor: 'hsl(var(--muted-foreground))',
      },
      disabledStyle: {
        backgroundColor: 'hsl(var(--muted))',
        color: 'hsl(var(--muted-foreground))',
        cursor: 'not-allowed',
        opacity: 0.6,
      },
    },
    last: {
      enabled: true,
      text: 'Last',
      icon: '⟫',
      style: {
        padding: '8px 12px',
        backgroundColor: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
        borderRadius: '6px',
        color: 'hsl(var(--foreground))',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 150ms ease',
      },
      hoverStyle: {
        backgroundColor: 'hsl(var(--accent))',
        borderColor: 'hsl(var(--muted-foreground))',
      },
      disabledStyle: {
        backgroundColor: 'hsl(var(--muted))',
        color: 'hsl(var(--muted-foreground))',
        cursor: 'not-allowed',
        opacity: 0.6,
      },
    },
    pageNumber: {
      style: {
        padding: '8px 12px',
        backgroundColor: 'transparent',
        border: '1px solid hsl(var(--border))',
        borderRadius: '6px',
        color: 'hsl(var(--foreground))',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 150ms ease',
        minWidth: '40px',
        textAlign: 'center',
      },
      activeStyle: {
        backgroundColor: 'hsl(var(--primary))',
        borderColor: 'hsl(var(--primary))',
        color: 'hsl(var(--primary-foreground))',
        fontWeight: '600',
      },
      hoverStyle: {
        backgroundColor: 'hsl(var(--accent))',
        borderColor: 'hsl(var(--muted-foreground))',
      },
    },
    ellipsis: {
      style: {
        padding: '8px 4px',
        color: 'hsl(var(--muted-foreground))',
        fontSize: '14px',
        userSelect: 'none',
      },
      text: '…',
    },
  },

  // Display formats
  display: {
    itemCounts: {
      format: 'detailed',
      templates: {
        simple: '{start}-{end} of {total}',
        detailed: 'Showing {start} to {end} of {total} results',
        compact: '{start}-{end}/{total}',
      },
      position: 'bottom',
    },
    pageNumbers: {
      format: 'numbers',
      showEllipsis: true,
      ellipsisThreshold: 7,
      maxVisible: 5,
    },
    pageSize: {
      label: 'Show',
      suffix: 'per page',
      position: 'left',
    },
  },

  // Behavior settings
  behavior: {
    jumpToPage: {
      enabled: true,
      trigger: 'click',
      inputType: 'number',
      validation: true,
      placeholder: 'Go to page...',
    },
    infiniteScroll: {
      enabled: false,
      threshold: 10,
      loadingBuffer: 25,
      maxAutoLoad: 500,
      loadingIndicator: true,
    },
    keyboard: {
      enabled: true,
      shortcuts: {
        nextPage: ['ArrowRight', 'PageDown'],
        previousPage: ['ArrowLeft', 'PageUp'],
        firstPage: ['Home', 'Ctrl+ArrowLeft'],
        lastPage: ['End', 'Ctrl+ArrowRight'],
        jumpToPage: ['Ctrl+G', 'F5'],
      },
    },
    url: {
      updateUrl: true,
      pageParam: 'page',
      sizeParam: 'size',
      preserveOtherParams: true,
    },
  },

  // Styling variants
  variants: {
    default: {
      container: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '16px',
        backgroundColor: 'transparent',
        borderTop: '1px solid hsl(var(--border))',
      },
      spacing: '8px',
      alignment: 'space-between',
    },
    compact: {
      container: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '8px',
        backgroundColor: 'transparent',
      },
      spacing: '4px',
      alignment: 'center',
    },
    minimal: {
      container: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 0',
        backgroundColor: 'transparent',
      },
      spacing: '12px',
      alignment: 'center',
    },
    detailed: {
      container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '20px',
        backgroundColor: 'hsl(var(--muted))',
        borderRadius: '8px',
        border: '1px solid hsl(var(--border))',
      },
      spacing: '16px',
      alignment: 'center',
    },
  },

  // Responsive behavior
  responsive: {
    breakpoints: {
      mobile: 640,
      tablet: 768,
      desktop: 1024,
    },
    adaptations: {
      mobile: {
        showFirstLast: false,
        showPageNumbers: true,
        maxVisiblePages: 3,
        compactLabels: true,
      },
      tablet: {
        showFirstLast: true,
        showPageNumbers: true,
        maxVisiblePages: 5,
        compactLabels: false,
      },
    },
  },

  // Loading and error states
  states: {
    loading: {
      showSkeleton: true,
      skeletonStyle: {
        height: '40px',
        backgroundColor: 'hsl(var(--muted))',
        borderRadius: '6px',
        animation: 'pulse 1.5s ease-in-out infinite',
      },
      loadingText: 'Loading...',
      showProgress: false,
    },
    error: {
      showRetry: true,
      retryText: 'Retry',
      errorMessageStyle: {
        color: 'hsl(var(--destructive))',
        fontSize: '14px',
        padding: '8px',
        textAlign: 'center',
      },
    },
    empty: {
      showMessage: true,
      emptyText: 'No items to display',
      showCreateButton: false,
      createButtonText: 'Create New',
    },
  },
};

/**
 * Utility function to get page size options based on variant
 */
export function getPageSizeOptions(variant: keyof typeof paginationConfig.pageSizeOptions = 'default'): number[] {
  return paginationConfig.pageSizeOptions[variant];
}

/**
 * Utility function to calculate pagination info
 */
export function calculatePagination(currentPage: number, pageSize: number, totalItems: number) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);
  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;
  
  return {
    currentPage,
    totalPages,
    pageSize,
    totalItems,
    start,
    end,
    hasNext,
    hasPrevious,
    isEmpty: totalItems === 0,
  };
}

/**
 * Utility function to generate visible page numbers
 */
export function getVisiblePages(
  currentPage: number,
  totalPages: number,
  maxVisible: number = paginationConfig.defaults.maxVisiblePages
): (number | 'ellipsis')[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const halfVisible = Math.floor(maxVisible / 2);

  // Always show first page
  pages.push(1);

  if (currentPage <= halfVisible + 1) {
    // Show pages from start
    for (let i = 2; i <= maxVisible - 1; i++) {
      pages.push(i);
    }
    if (maxVisible < totalPages) {
      pages.push('ellipsis');
    }
  } else if (currentPage >= totalPages - halfVisible) {
    // Show pages at end
    if (totalPages > maxVisible) {
      pages.push('ellipsis');
    }
    for (let i = totalPages - maxVisible + 2; i <= totalPages - 1; i++) {
      pages.push(i);
    }
  } else {
    // Show pages around current
    pages.push('ellipsis');
    for (let i = currentPage - halfVisible + 1; i <= currentPage + halfVisible - 1; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
  }

  // Always show last page (if more than 1 page)
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

/**
 * Utility function to format item count display
 */
export function formatItemCount(
  start: number,
  end: number,
  total: number,
  format: keyof typeof paginationConfig.display.itemCounts.templates = 'detailed'
): string {
  const template = paginationConfig.display.itemCounts.templates[format];
  return template
    .replace('{start}', start.toString())
    .replace('{end}', end.toString())
    .replace('{total}', total.toString());
}

/**
 * CSS class names for pagination styling
 */
export const paginationClasses = {
  container: 'app-pagination',
  containerDefault: 'app-pagination--default',
  containerCompact: 'app-pagination--compact',
  containerMinimal: 'app-pagination--minimal',
  containerDetailed: 'app-pagination--detailed',
  
  navigation: 'app-pagination-nav',
  
  button: 'app-pagination-button',
  buttonFirst: 'app-pagination-button--first',
  buttonPrevious: 'app-pagination-button--previous',
  buttonNext: 'app-pagination-button--next',
  buttonLast: 'app-pagination-button--last',
  buttonPage: 'app-pagination-button--page',
  buttonActive: 'app-pagination-button--active',
  buttonDisabled: 'app-pagination-button--disabled',
  
  ellipsis: 'app-pagination-ellipsis',
  
  info: 'app-pagination-info',
  infoItemCount: 'app-pagination-info-count',
  infoPageSize: 'app-pagination-info-size',
  
  loading: 'app-pagination--loading',
  error: 'app-pagination--error',
  empty: 'app-pagination--empty',
} as const;